// src/pages/TradingBots.tsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import tradingBotService from "../../services/tradingBotService";
import botGif from "../../assets/bot.GIF";
import {
  formatCurrency,
  formatNumber,
  getXrpPrice,
  formatXRP,
} from "../../utils/formatters";

import {
  RocketLaunchIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  StarIcon,
  FireIcon,
  BoltIcon,
  SparklesIcon,
  CalendarIcon,
  PlusCircleIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";

// ── Types ────────────────────────────────────────────────────────────────────

interface UserBotData {
  total_earnings: number;
  joined_at: string;
  expires_at: string | null;
  capital_invested: number;
  days_remaining: number | null;
}

interface TradingBot {
  id: number;
  name: string;
  description: string;
  daily_rate: number;
  min_balance: number;
  max_balance: number | null;
  price: number;
  is_free: boolean;
  duration_days: number;
  duration_label: string;
  return_capital: boolean;
  max_users: number;
  current_users: number;
  spots_left: number | null;
  min_referrals: number;
  referral_bonus: number;
  image_url: string | null;
  color: string;
  priority: number;
  can_join: boolean;
  user_balance: number;
  user_referrals: number;
  estimated_daily_earnings: number;
  estimated_monthly_earnings: number;
  estimated_daily_per_1000: number;
  // Multi-bot fields
  user_is_in_bot: boolean;
  user_has_active_bot: boolean;
  user_current_bot_id: number | null;
  user_bot_data?: UserBotData | null;
  // Returning customer bonus
  returning_customer_bonus: number | null;
  is_returning_customer: boolean;
}

/** Represents one active subscription returned from /trading/active-bots */
interface ActiveBotSubscription {
  id: number;
  subscription_id: number;
  name: string;
  description: string;
  daily_rate: number;
  min_balance: number;
  max_balance: number | null;
  price: number;
  duration_days: number;
  duration_label: string;
  return_capital: boolean;
  color: string;
  total_earnings: number;
  capital_invested: number;
  joined_at: string | null;
  expires_at: string | null;
  days_remaining: number | null;
  image_url: string | null;
  daily_rate_percentage?: string;
}

interface ActiveBotsSummary {
  total_bots: number;
  total_invested: number;
  total_daily_earnings: number;
  total_earnings_so_far: number;
}

// ── Loading component ─────────────────────────────────────────────────────────

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading trading bots...",
}) => (
  <div className="w-full h-64 bg-black">
    <div className="h-full flex flex-col gap-2 items-center justify-center bg-black/80">
      <svg className={`${globalStyle.loadingSpinner} mr-2`} viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="mt-4 text-gray-400">{message}</p>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const TradingBots: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [bots, setBots] = useState<TradingBot[]>([]);
  const [activeBots, setActiveBots] = useState<ActiveBotSubscription[]>([]);
  const [activeSummary, setActiveSummary] = useState<ActiveBotsSummary | null>(
    null,
  );
  const [loading, setLoading] = useState({
    bots: true,
    activeBots: true,
    overall: true,
  });
  const [joining, setJoining] = useState(false);
  const [leavingId, setLeavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<TradingBot | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [leavingSubscription, setLeavingSubscription] =
    useState<ActiveBotSubscription | null>(null);
  const [view, setView] = useState<"grid" | "list">("list");
  const [refreshing, setRefreshing] = useState(false);
  const [xrpPrice] = useState(getXrpPrice());
  const [investmentAmount, setInvestmentAmount] = useState<number>(0);
  const [amountError, setAmountError] = useState<string | null>(null);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line
  }, []);

  const loadAllData = async () => {
    try {
      setLoading({ bots: true, activeBots: true, overall: true });
      setError(null);
      await Promise.all([loadTradingBots(), loadActiveBots()]);
    } catch {
      setError("Failed to load trading data. Please try again later.");
    } finally {
      setLoading((p) => ({ ...p, overall: false }));
    }
  };

  const loadTradingBots = async () => {
    try {
      setLoading((p) => ({ ...p, bots: true }));
      const response = await tradingBotService.getAvailableBots();
      const data = response?.data || response;
      setBots(
        Array.isArray(data?.bots) ? data.bots : Array.isArray(data) ? data : [],
      );
    } catch {
      setError("Failed to load trading bots.");
      setBots([]);
    } finally {
      setLoading((p) => ({ ...p, bots: false }));
    }
  };

  const loadActiveBots = async () => {
    try {
      setLoading((p) => ({ ...p, activeBots: true }));
      const response = await tradingBotService.getActiveBots();
      const data = response?.data || response;
      setActiveBots(Array.isArray(data?.bots) ? data.bots : []);
      setActiveSummary(data?.summary ?? null);
    } catch {
      setActiveBots([]);
      setActiveSummary(null);
    } finally {
      setLoading((p) => ({ ...p, activeBots: false }));
    }
  };

  const handleJoinBot = async () => {
    if (!selectedBot) return;
    try {
      setJoining(true);
      setError(null);
      setAmountError(null);
      const response = await tradingBotService.joinBot(
        selectedBot.id,
        investmentAmount,
      );
      const data = response?.data || response;
      setSuccess(data?.message || "Successfully joined trading bot!");
      setShowJoinModal(false);
      setSelectedBot(null);
      setInvestmentAmount(0);
      await loadAllData();
    } catch (e: any) {
      setError(
        e.response?.data?.message || "Failed to join bot. Please try again.",
      );
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveBot = async () => {
    if (!leavingSubscription) return;
    try {
      setLeavingId(leavingSubscription.subscription_id);
      setError(null);
      const response = await tradingBotService.leaveBot(
        leavingSubscription.id,
        leavingSubscription.subscription_id,
      );
      const data = response?.data || response;
      setSuccess(data?.message || "Successfully left trading bot!");
      setLeavingSubscription(null);
      await loadAllData();
    } catch (e: any) {
      setError(
        e.response?.data?.message || "Failed to leave bot. Please try again.",
      );
    } finally {
      setLeavingId(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const openJoinModal = (bot: TradingBot) => {
    setSelectedBot(bot);
    setInvestmentAmount(bot.min_balance);
    setAmountError(null);
    setShowJoinModal(true);
  };

  const validateAmount = (amount: number, bot: TradingBot): string | null => {
    if (amount < bot.min_balance)
      return `Minimum investment is ${formatXRP(bot.min_balance)} XRP`;
    if (bot.max_balance !== null && amount > bot.max_balance)
      return `Maximum investment is ${formatXRP(bot.max_balance)} XRP`;
    if (amount > (user?.xrp_balance || 0))
      return `Insufficient balance. You have ${formatXRP(user?.xrp_balance || 0)} XRP`;
    return null;
  };

  const handleAmountChange = (value: string) => {
    if (!selectedBot) return;
    const numValue = parseFloat(value) || 0;
    setInvestmentAmount(numValue);
    setAmountError(validateAmount(numValue, selectedBot));
  };

  const getBotIcon = (botName: string) => {
    const n = botName.toLowerCase();
    if (n.includes("pro") || n.includes("premium")) return RocketLaunchIcon;
    if (n.includes("starter") || n.includes("basic")) return BoltIcon;
    if (n.includes("elite") || n.includes("vip")) return StarIcon;
    if (n.includes("turbo") || n.includes("fast")) return FireIcon;
    if (n.includes("advanced") || n.includes("master")) return ChartBarIcon;
    return SparklesIcon;
  };

  const formatExpiry = (expiresAt: string | null): string =>
    expiresAt ? new Date(expiresAt).toLocaleDateString() : "No expiry";

  const getDaysRemainingBadgeColor = (days: number | null): string => {
    if (days === null) return "text-gray-400";
    if (days <= 1) return "text-red-400";
    if (days <= 3) return "text-yellow-400";
    return "text-green-400";
  };

  const getProgressPercent = (sub: ActiveBotSubscription): number => {
    if (!sub.expires_at || !sub.joined_at) return 0;
    const total =
      new Date(sub.expires_at).getTime() - new Date(sub.joined_at).getTime();
    const elapsed = Date.now() - new Date(sub.joined_at).getTime();
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  const calcDailyEarnings = (amount: number, bot: TradingBot) =>
    amount * (bot.daily_rate / 100);

  const calcTotalEarnings = (amount: number, bot: TradingBot) =>
    amount * (bot.daily_rate / 100) * bot.duration_days;

  // ── Active bots banner (multi) ────────────────────────────────────────────

  const renderActiveBotsBanner = () => {
    if (loading.activeBots) {
      return (
        <div className="mb-6">
          <Card className="h-24 flex items-center justify-center">
            <p className="text-gray-400 text-sm">Loading active bots...</p>
          </Card>
        </div>
      );
    }

    if (activeBots.length === 0) return null;

    return (
      <div className="mb-6 space-y-3">
        {/* Summary bar */}
        {activeSummary && activeSummary.total_bots > 1 && (
          <Card className="border-l-0 bg-green-900/10">
            <div className="flex flex-wrap gap-6 items-center justify-between">
              <p className="text-sm font-semibold text-green-300">
                {activeSummary.total_bots} Active Bots
              </p>
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <p className="text-gray-400 text-xs">Total Invested</p>
                  <p className="font-bold text-white">
                    {formatXRP(activeSummary.total_invested)} XRP
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Daily Earnings</p>
                  <p className="font-bold text-green-400">
                    +{formatXRP(activeSummary.total_daily_earnings)} XRP
                    <span className="ml-1 text-gray-400 text-xs">
                      (
                      {formatCurrency(
                        activeSummary.total_daily_earnings * xrpPrice,
                      )}
                      /day)
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Total Earned</p>
                  <p className="font-bold text-green-400">
                    {formatXRP(activeSummary.total_earnings_so_far)} XRP
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Individual active bot cards */}
        {activeBots.map((sub) => {
          const Icon = getBotIcon(sub.name);
          const dailyEarnings =
            (sub.capital_invested || 0) * (sub.daily_rate / 100);
          return (
            <Card key={sub.subscription_id} className="border-l-0">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Identity */}
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: `${sub.color}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: sub.color }} />
                  </div>
                  <div>
                    <h2 className={`${globalStyle.h5} text-white`}>
                      {sub.name}
                    </h2>
                    <p className="text-sm text-gray-400">
                      Active • {formatNumber(sub.daily_rate, 4)}% daily
                      {sub.capital_invested > 0 &&
                        ` • Invested: ${formatXRP(sub.capital_invested)} XRP`}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center text-xs text-gray-400">
                        <CalendarIcon className="w-3 h-3 mr-1" />
                        Expires: {formatExpiry(sub.expires_at)}
                      </span>
                      {sub.days_remaining !== null && (
                        <span
                          className={`text-xs font-semibold ${getDaysRemainingBadgeColor(sub.days_remaining)}`}
                        >
                          {sub.days_remaining === 0
                            ? "Expires today!"
                            : `${sub.days_remaining}d left`}
                        </span>
                      )}
                      {sub.return_capital && (
                        <span className="text-xs text-blue-400 flex items-center">
                          <ShieldCheckIcon className="w-3 h-3 mr-1" />
                          Capital returns
                        </span>
                      )}
                    </div>
                    {sub.expires_at && sub.joined_at && (
                      <div className="mt-2 w-full bg-gray-700 rounded-full h-1.5 max-w-xs">
                        <div
                          className="h-1.5 rounded-full bg-gradient-to-r from-green-500 to-yellow-400"
                          style={{ width: `${getProgressPercent(sub)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats + Leave */}
                <div className="flex items-center space-x-4 max-sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-300">
                      Daily Earnings
                    </p>
                    <p className="text-lg font-bold text-white">
                      +{formatXRP(dailyEarnings)} XRP
                    </p>
                    <p className="text-sm text-gray-400">
                      {formatCurrency(dailyEarnings * xrpPrice)}/day
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-300">
                      Total Earned
                    </p>
                    <p className="text-lg font-bold text-green-400">
                      {formatXRP(sub.total_earnings)} XRP
                    </p>
                    <p className="text-sm text-gray-400">
                      {formatCurrency(sub.total_earnings * xrpPrice)}
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setLeavingSubscription(sub)}
                    loading={leavingId === sub.subscription_id}
                  >
                    Leave
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={globalStyle.container}>
      <Header />
      <main className="page-content">
        {/* Active bots banners */}
        {renderActiveBotsBanner()}

        {/* Page header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between">
          <div>
            <h1 className={globalStyle.h3}>Trading Bots</h1>
            <p className={`${globalStyle.body2} text-gray-400`}>
              Run multiple bots simultaneously • 1 XRP ={" "}
              {formatCurrency(xrpPrice)}
            </p>
          </div>
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleRefresh}
              loading={refreshing}
            >
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </Button>
            <div className="bg-gray-800 rounded-lg p-1 flex">
              {(["grid", "list"] as const).map((v) => (
                <button
                  key={v}
                  className={`px-3 py-1 rounded text-sm ${view === v ? "bg-gray-700 text-white" : "text-gray-400"}`}
                  onClick={() => setView(v)}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError(null)}
            className="mb-6"
          />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
            className="mb-6"
          />
        )}

        {/* Bot list */}
        {loading.bots ? (
          <LoadingScreen message="Loading available trading bots..." />
        ) : bots.length === 0 ? (
          <Card className="text-center py-12">
            <RocketLaunchIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className={globalStyle.h5}>No Trading Bots Available</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Check back later.
            </p>
          </Card>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bots
              .slice()
              .reverse()
              .map((bot) => {
                const Icon = getBotIcon(bot.name);
                const isUserInBot = bot.user_is_in_bot;
                const canJoin = bot.can_join;
                // bonusAmount: the configured bonus (show as offer on card even before user qualifies)
                // willGetBonus: user has previously been in this bot so they WILL receive it on invest
                const bonusAmount = bot.returning_customer_bonus ?? 0;
                const willGetBonus =
                  bot.is_returning_customer && bonusAmount > 0;

                return (
                  <div key={bot.id} className="relative">
                    <Card className="border-l-0">
                      {bot.priority > 0 && (
                        <div className="absolute top-4 right-4">
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-900 text-yellow-300 rounded-full">
                            {bot.priority === 1 ? "Premium" : "VIP"}
                          </span>
                        </div>
                      )}

                      <div className="flex items-start mb-4">
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center mr-4"
                          style={{ backgroundColor: `${bot.color}20` }}
                        >
                          <Icon
                            className="w-7 h-7"
                            style={{ color: bot.color }}
                          />
                        </div>
                        <div>
                          <h3 className={`${globalStyle.h6} text-white`}>
                            {bot.name}
                          </h3>
                          <p className="text-sm text-gray-400 line-clamp-2">
                            {bot.description}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">
                            Daily Rate
                          </span>
                          <span className="font-medium text-green-500">
                            {formatNumber(bot.daily_rate, 4)}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">
                            Investment Range
                          </span>
                          <span className="font-medium text-white">
                            {formatXRP(bot.min_balance)} –{" "}
                            {bot.max_balance ? formatXRP(bot.max_balance) : "∞"}{" "}
                            XRP
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">
                            Duration
                          </span>
                          <span className="font-medium text-white">
                            {bot.duration_label}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-400">
                            Capital Return
                          </span>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              bot.return_capital
                                ? "bg-blue-900 text-blue-300"
                                : "bg-gray-700 text-gray-400"
                            }`}
                          >
                            {bot.return_capital ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-800 rounded-lg p-3 mb-4">
                        <p className="text-xs text-gray-400 mb-2">
                          Earnings per 1000 XRP
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-gray-400">Daily</p>
                            <p className="font-medium text-green-500">
                              +{formatXRP(bot.estimated_daily_per_1000)} XRP
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">
                              Over {bot.duration_label}
                            </p>
                            <p className="font-medium text-green-500">
                              +
                              {formatXRP(
                                bot.estimated_daily_per_1000 *
                                  bot.duration_days,
                              )}{" "}
                              XRP
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Returning customer bonus badge */}
                      {bonusAmount > 0 && (
                        <div className="mb-3 flex items-center gap-2 bg-yellow-900/20 border border-yellow-800/40 rounded-lg px-3 py-2">
                          <GiftIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                          <span className="text-xs text-yellow-300">
                            {willGetBonus
                              ? `Welcome back! +${formatXRP(bonusAmount)} XRP bonus on invest`
                              : `Loyalty bonus: +${formatXRP(bonusAmount)} XRP for returning investors`}
                          </span>
                        </div>
                      )}

                      {/* Active badge if user already has this bot */}
                      {isUserInBot && bot.user_bot_data && (
                        <div className="mb-3 bg-green-900 border border-green-800 rounded-lg p-2 text-center">
                          <CheckCircleIcon className="w-4 h-4 text-green-400 inline mr-1" />
                          <span className="text-xs text-green-300">
                            Already active —{" "}
                            {formatXRP(bot.user_bot_data.capital_invested)} XRP
                          </span>
                        </div>
                      )}

                      {canJoin ? (
                        <Button fullWidth onClick={() => openJoinModal(bot)}>
                          <PlusCircleIcon className="w-4 h-4 mr-2" />
                          {willGetBonus
                            ? `Invest & Get ${formatXRP(bonusAmount)} XRP Back`
                            : isUserInBot
                              ? "Invest Again"
                              : "Invest Now"}
                        </Button>
                      ) : (
                        <Button variant="tertiary" fullWidth disabled>
                          <XCircleIcon className="w-4 h-4 mr-2" />
                          Requirements not met
                        </Button>
                      )}
                    </Card>
                  </div>
                );
              })}
          </div>
        ) : (
          // LIST VIEW
          <div className="space-y-4">
            {bots
              .slice()
              .reverse()
              .map((bot) => {
                const Icon = getBotIcon(bot.name);
                const isUserInBot = bot.user_is_in_bot;
                const canJoin = bot.can_join;
                const bonusAmount = bot.returning_customer_bonus ?? 0;
                const willGetBonus =
                  bot.is_returning_customer && bonusAmount > 0;

                return (
                  <Card key={bot.id} className="border-l-0">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Identity */}
                      <div className="flex items-center md:w-1/4">
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                          style={{ backgroundColor: `${bot.color}20` }}
                        >
                          <Icon
                            className="w-6 h-6"
                            style={{ color: bot.color }}
                          />
                        </div>
                        <div>
                          <h3 className={`${globalStyle.h6} text-white`}>
                            {bot.name}
                          </h3>
                          <p className="text-sm text-gray-400">
                            {formatNumber(bot.daily_rate, 4)}% daily •{" "}
                            {bot.duration_label}
                          </p>
                          {isUserInBot && (
                            <span className="text-xs text-green-400 flex items-center gap-1 mt-0.5">
                              <CheckCircleIcon className="w-3 h-3" /> Active
                            </span>
                          )}
                          {bonusAmount > 0 && (
                            <span className="text-xs text-yellow-400 flex items-center gap-1 mt-0.5">
                              <GiftIcon className="w-3 h-3" />
                              {willGetBonus
                                ? "Your bonus: "
                                : "Loyalty bonus: "}
                              +{formatXRP(bonusAmount)} XRP
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="md:w-2/4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-gray-400">Range</p>
                          <p className="font-medium text-white">
                            {formatXRP(bot.min_balance)} –{" "}
                            {bot.max_balance ? formatXRP(bot.max_balance) : "∞"}{" "}
                            XRP
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">
                            Capital Return
                          </p>
                          <p
                            className={`font-medium ${bot.return_capital ? "text-blue-400" : "text-gray-400"}`}
                          >
                            {bot.return_capital ? "Yes" : "No"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">
                            Daily / 1000 XRP
                          </p>
                          <p className="font-medium text-green-500">
                            +{formatXRP(bot.estimated_daily_per_1000)} XRP
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">
                            Total / 1000 XRP
                          </p>
                          <p className="font-medium text-green-500">
                            +
                            {formatXRP(
                              bot.estimated_daily_per_1000 * bot.duration_days,
                            )}{" "}
                            XRP
                          </p>
                        </div>
                      </div>

                      {/* Action */}
                      <div className="md:w-1/4">
                        {canJoin ? (
                          <Button fullWidth onClick={() => openJoinModal(bot)}>
                            <PlusCircleIcon className="w-4 h-4 mr-2" />
                            {willGetBonus
                              ? `Invest & Get ${formatXRP(bonusAmount)} XRP Back`
                              : isUserInBot
                                ? "Invest Again"
                                : "Invest Now"}
                          </Button>
                        ) : (
                          <div className="text-center">
                            <Button variant="tertiary" fullWidth disabled>
                              Not Available
                            </Button>
                            <p className="text-xs text-red-400 mt-1">
                              Requirements not met
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
          </div>
        )}
      </main>

      <BottomNav />

      {/* Single floating bot gif with active plan count */}
      {activeBots.length > 0 && (
        <div className="fixed bottom-20 right-5 md:right-20 z-30 pointer-events-none">
          <div className="animate-float relative">
            <img
              src={botGif}
              alt="Bots Active"
              className="w-12 h-12 md:w-16 md:h-16 opacity-90 drop-shadow-lg"
            />
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-green-600 text-white text-[7px] font-medium px-2 py-1 rounded-full shadow-lg">
              {activeBots.length === 1
                ? `${activeBots[0].name}`
                : `${activeBots.length} Bots Active`}
            </div>
          </div>
        </div>
      )}

      {/* ── Join Modal ── */}
      <Modal
        isOpen={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setSelectedBot(null);
          setInvestmentAmount(0);
          setAmountError(null);
        }}
        title="Invest in Trading Bot"
        size="md"
      >
        {selectedBot && (
          <div className="space-y-6">
            <div className="text-center">
              <div
                className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${selectedBot.color}20` }}
              >
                {(() => {
                  const I = getBotIcon(selectedBot.name);
                  return (
                    <I
                      className="w-10 h-10"
                      style={{ color: selectedBot.color }}
                    />
                  );
                })()}
              </div>
              <h3 className={globalStyle.h5}>{selectedBot.name}</h3>
              <p className={`${globalStyle.body2} mt-1 text-gray-400`}>
                {selectedBot.description}
              </p>
              {selectedBot.user_is_in_bot && (
                <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-green-900/40 border border-green-700 rounded-full text-xs text-green-300">
                  <CheckCircleIcon className="w-3 h-3" />
                  You already have an active subscription in this bot
                </div>
              )}
            </div>

            {/* Returning customer callout — shown above bot stats */}
            {selectedBot.returning_customer_bonus != null &&
              selectedBot.returning_customer_bonus > 0 && (
                <div className="flex items-start gap-3 bg-yellow-900/20 border border-yellow-800/40 rounded-xl p-4">
                  <GiftIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    {selectedBot.is_returning_customer ? (
                      <>
                        <p className="text-sm font-semibold text-yellow-300">
                          Welcome back bonus!
                        </p>
                        <p className="text-xs text-yellow-200 mt-0.5">
                          You'll receive an instant{" "}
                          <span className="font-bold">
                            {formatXRP(selectedBot.returning_customer_bonus)}{" "}
                            XRP
                          </span>{" "}
                          bonus the moment your investment is confirmed.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-yellow-300">
                          Loyalty bonus available
                        </p>
                        <p className="text-xs text-yellow-200 mt-0.5">
                          Re-invest in this plan after leaving to earn a{" "}
                          <span className="font-bold">
                            {formatXRP(selectedBot.returning_customer_bonus)}{" "}
                            XRP
                          </span>{" "}
                          returning customer bonus instantly.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

            <div className="bg-gray-800 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400">Daily Rate</p>
                  <p className="font-bold text-green-400 text-lg">
                    {formatNumber(selectedBot.daily_rate, 4)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Duration</p>
                  <p className="font-bold text-white text-lg">
                    {selectedBot.duration_label}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Investment Range</p>
                  <p className="font-bold text-white text-sm">
                    {formatXRP(selectedBot.min_balance)} –{" "}
                    {selectedBot.max_balance
                      ? formatXRP(selectedBot.max_balance)
                      : "∞"}{" "}
                    XRP
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Capital Return</p>
                  <p
                    className={`font-bold text-sm ${selectedBot.return_capital ? "text-blue-400" : "text-gray-400"}`}
                  >
                    {selectedBot.return_capital ? "✓ Yes" : "✗ No"}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <label className={globalStyle.label}>
                Investment Amount (XRP)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.000001"
                  min={selectedBot.min_balance}
                  max={selectedBot.max_balance || undefined}
                  value={investmentAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className={`${globalStyle.input} pl-8 ${amountError ? "border-red-500 focus:ring-red-500" : ""}`}
                  placeholder={`Min: ${selectedBot.min_balance} XRP`}
                />
                <CurrencyDollarIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  onClick={() =>
                    handleAmountChange(selectedBot.min_balance.toString())
                  }
                  className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 rounded-full text-gray-300"
                >
                  Min
                </button>
                {selectedBot.max_balance && (
                  <>
                    <button
                      onClick={() =>
                        handleAmountChange(
                          (
                            (selectedBot.min_balance +
                              selectedBot.max_balance!) /
                            2
                          ).toString(),
                        )
                      }
                      className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 rounded-full text-gray-300"
                    >
                      Mid
                    </button>
                    <button
                      onClick={() =>
                        handleAmountChange(selectedBot.max_balance!.toString())
                      }
                      className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 rounded-full text-gray-300"
                    >
                      Max
                    </button>
                  </>
                )}
              </div>
              {amountError && (
                <p className="text-sm text-red-400">{amountError}</p>
              )}
            </div>

            <div
              className={`rounded-lg p-4 border ${
                investmentAmount <= (user?.xrp_balance || 0) &&
                investmentAmount >= selectedBot.min_balance
                  ? "bg-green-900/20 border-green-800/40"
                  : "bg-red-900/20 border-red-800/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Your Balance</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {formatXRP(user?.xrp_balance || 0)} XRP
                  </span>
                  {investmentAmount <= (user?.xrp_balance || 0) &&
                  investmentAmount >= selectedBot.min_balance ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-400" />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-gray-300">Investment</span>
                <span className="font-medium text-yellow-300">
                  -{formatXRP(investmentAmount)} XRP
                </span>
              </div>
              {investmentAmount <= (user?.xrp_balance || 0) &&
                investmentAmount >= selectedBot.min_balance && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                    <span className="text-sm text-gray-300">Balance After</span>
                    <span className="font-medium text-white">
                      {formatXRP((user?.xrp_balance || 0) - investmentAmount)}{" "}
                      XRP
                    </span>
                  </div>
                )}
            </div>

            {selectedBot.return_capital && (
              <div className="bg-blue-900/20 border border-blue-800/40 rounded-lg p-3">
                <p className="text-sm text-blue-300 font-medium">
                  💡 Capital Return
                </p>
                <p className="text-xs text-blue-200 mt-1">
                  Your invested capital of {formatXRP(investmentAmount)} XRP
                  will be returned after the {selectedBot.duration_label}{" "}
                  subscription ends.
                </p>
              </div>
            )}

            <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-300 mb-3">
                Estimated Returns for {formatXRP(investmentAmount)} XRP
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-blue-200">Daily</p>
                  <p className="text-lg font-bold text-white">
                    +
                    {formatXRP(
                      calcDailyEarnings(investmentAmount, selectedBot),
                    )}{" "}
                    XRP
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatCurrency(
                      calcDailyEarnings(investmentAmount, selectedBot) *
                        xrpPrice,
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-200">
                    Over {selectedBot.duration_label}
                  </p>
                  <p className="text-lg font-bold text-white">
                    +
                    {formatXRP(
                      calcTotalEarnings(investmentAmount, selectedBot),
                    )}{" "}
                    XRP
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatCurrency(
                      calcTotalEarnings(investmentAmount, selectedBot) *
                        xrpPrice,
                    )}
                  </p>
                </div>
              </div>

              {/* Day-1 bonus line — only shown when eligible */}
              {selectedBot.is_returning_customer &&
                selectedBot.returning_customer_bonus &&
                selectedBot.returning_customer_bonus > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-800/40 flex items-center justify-between">
                    <span className="text-xs text-yellow-300 flex items-center gap-1">
                      <GiftIcon className="w-3 h-3" /> Day 1 total (incl.
                      welcome bonus)
                    </span>
                    <span className="text-sm font-bold text-yellow-300">
                      +
                      {formatXRP(
                        calcDailyEarnings(investmentAmount, selectedBot) +
                          selectedBot.returning_customer_bonus,
                      )}{" "}
                      XRP
                    </span>
                  </div>
                )}
            </div>

            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => {
                  setShowJoinModal(false);
                  setSelectedBot(null);
                  setInvestmentAmount(0);
                }}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleJoinBot}
                loading={joining}
                disabled={
                  !!amountError ||
                  investmentAmount < selectedBot.min_balance ||
                  (selectedBot.max_balance !== null &&
                    investmentAmount > selectedBot.max_balance) ||
                  investmentAmount > (user?.xrp_balance || 0)
                }
                fullWidth
              >
                {selectedBot.is_returning_customer &&
                selectedBot.returning_customer_bonus &&
                selectedBot.returning_customer_bonus > 0
                  ? `Invest & Claim ${formatXRP(selectedBot.returning_customer_bonus)} XRP`
                  : `Invest ${formatXRP(investmentAmount)} XRP`}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Leave Modal ── */}
      <Modal
        isOpen={!!leavingSubscription}
        onClose={() => setLeavingSubscription(null)}
        title="Leave Trading Bot"
        size="sm"
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
              <XCircleIcon className="w-8 h-8 text-red-300" />
            </div>
            <h3 className={globalStyle.h5}>Leave Bot?</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Are you sure you want to leave {leavingSubscription?.name}? You
              will stop receiving daily earnings immediately.
            </p>
          </div>

          {leavingSubscription &&
            leavingSubscription.capital_invested > 0 &&
            !leavingSubscription.return_capital && (
              <div className="bg-red-900/30 border border-red-800 rounded-lg p-4">
                <p className="text-sm font-medium text-red-300">⚠️ Warning</p>
                <p className="text-sm text-red-200 mt-1">
                  Your invested capital of{" "}
                  {formatXRP(leavingSubscription.capital_invested)} XRP will NOT
                  be returned as this bot does not have capital return enabled.
                </p>
              </div>
            )}

          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() => setLeavingSubscription(null)}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleLeaveBot}
              loading={leavingId !== null}
              fullWidth
            >
              Leave Bot
            </Button>
          </div>
        </div>
      </Modal>

      <style>{`
        .animate-float { animation: float 3s ease-in-out infinite; }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
    </div>
  );
};

export default TradingBots;
